import { OpenInNew } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Tooltip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { buildGalleryUrl } from 'src/util/galleryUrlBuilder';

type SetDefinition = Models.SetsPlayground.SetDefinition;

interface PreviewGalleryButtonProps {
    productKey: string;
    productVersion: string | null;
    cultures: string[];
    sets: SetDefinition[];
    isLoadingVersion: boolean;
}

export const PreviewGalleryButton = ({
    productKey,
    productVersion,
    cultures,
    sets,
    isLoadingVersion,
}: PreviewGalleryButtonProps): JSX.Element => {
    const [selectedLocale, setSelectedLocale] = useState<string>(
        cultures[0] ?? '',
    );

    useEffect(() => {
        if (cultures.length > 0 && !cultures.includes(selectedLocale)) {
            setSelectedLocale(cultures[0]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cultures]);

    const handlePreview = (): void => {
        if (sets.length === 0 || !selectedLocale) {
            return;
        }

        const url = buildGalleryUrl(
            selectedLocale,
            productKey,
            productVersion,
            undefined,
            sets,
        );

        window.open(url, '_blank');
    };

    const handleLocaleChange = (event: SelectChangeEvent<string>): void => {
        setSelectedLocale(event.target.value);
    };

    const isButtonDisabled = sets.length === 0
        || isLoadingVersion
        || cultures.length === 0
        || !selectedLocale
        || !productVersion;

    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            {/* Culture picker dropdown (Takes from available cultures) */}
            <Tooltip disableInteractive placement="top" title="Select culture to preview in gallery">
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="preview-locale-label">Preview Culture</InputLabel>
                    <Select
                        disabled={isLoadingVersion || cultures.length < 1 || sets.length === 0}
                        label="Preview Culture"
                        labelId="preview-locale-label"
                        value={selectedLocale}
                        onChange={handleLocaleChange}
                    >
                        {cultures.map((culture) => (
                            <MenuItem key={culture} value={culture}>
                                {culture}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Tooltip>

            {/* Preview button */}
            <Tooltip disableInteractive placement="top" title={'Preview this group\'s sets in a gallery'}>
                <span>
                    {/* Wrapper span needed for tooltip on disabled button */}
                    <Button
                        color="primary"
                        disabled={isButtonDisabled}
                        endIcon={
                            isLoadingVersion ? (
                                <CircularProgress size={16} />
                            ) : (
                                <OpenInNew />
                            )
                        }
                        size="medium"
                        variant="outlined"
                        onClick={handlePreview}
                    >
                        Preview in Gallery
                    </Button>
                </span>
            </Tooltip>
        </Box>
    );
};
